/**
 * Adds the subcategories admins added (Courses page sidebar > "+ Add Subcategory") to the Learning
 * Zone sidebar, after the rows already there: under their category's group (English > Poem), or
 * under the row they were added to (English > Action Songs > Hindi Songs), at any depth. Every
 * existing row stays as it is. A row shows the published courses placed in it, like a task type row.
 */
import { addCourseTaskTypeRows } from './courseTaskTypeRows';
import { ACTION_SONG_VIEWS, ART_VIEWS, STORY_VIEWS } from '../../../data/learningZoneMockData';

// Sidebar group id -> the admin course category whose subcategories it lists.
export const GROUP_CATEGORIES = {
  english: 'English',
  'computer-apps': 'Computer Apps',
  'life-skills': 'Life Skills',
  stories: 'Stories',
  'art-therapy': 'Art',
};

const lower = (value) => String(value || '').trim().toLowerCase();
// Each lesson keeps the name of the row it is placed in (`rowLabel`): a row that also lists the
// lessons of the rows under it names the row of a title it shows twice (CourseContentView).
const fromRow = (chapters, label) => (chapters || []).map((chapter) => (chapter.rowLabel ? chapter : { ...chapter, rowLabel: label }));

/**
 * Returns the tree with the added subcategory rows, plus `views`: view id -> what that row shows
 * ({ title, category, taskType, chapters }). `subcategories` comes from GET .../learning-zone.
 * `groupCategories` maps group ids to categories (GROUP_CATEGORIES by default).
 */
export function addCustomSubcategoryRows(tree, subcategories = [], groupCategories = GROUP_CATEGORIES) {
  const views = {};

  const withRows = tree.map((group) => {
    const category = groupCategories[group.id];
    const mine = category ? subcategories.filter((sub) => lower(sub.category) === lower(category)) : [];
    if (!mine.length) return group;

    const existing = group.children || [];
    // Stories uses small rows; new rows match their siblings.
    const compact = existing.length > 0 && existing.every((child) => child.compact);
    const toRow = (sub, asCompact) => {
      const view = `subcategory:${sub._id}`;
      views[view] = { title: sub.name, category: sub.category, taskType: '', chapters: fromRow(sub.chapters, sub.name) };
      return {
        id: `subcategory-${sub._id}`,
        label: sub.name,
        icon: sub.icon || '📚',
        view,
        subcategoryId: String(sub._id),
        ...(asCompact ? { compact: true } : {}),
      };
    };

    // Rows added under another row, by that row's id (built-in) or subcategory id (added).
    const nested = new Map();
    mine
      .filter((sub) => sub.parent)
      .forEach((sub) => nested.set(sub.parent, [...(nested.get(sub.parent) || []), toRow(sub, false)]));
    const withNested = (row) => {
      const extra = nested.get(row.subcategoryId || row.id) || [];
      if (!row.children && !extra.length) return row;
      return { ...row, children: [...(row.children || []), ...extra].map(withNested) };
    };

    const topLevel = mine.filter((sub) => !sub.parent).map((sub) => toRow(sub, compact));
    return { ...group, children: [...existing, ...topLevel].map(withNested) };
  });

  return { tree: withRows, views };
}

// Listing screens (scenes, songs, stories, Art Therapy's lists): they always show the published
// content placed in them, or "No lessons have been added here yet." - never sample items. Other
// screens (GCompris, Medical, Diary ...) are never replaced.
const LISTING_VIEWS = new Set([
  'scenes',
  ...Object.keys(ACTION_SONG_VIEWS),
  ...Object.keys(STORY_VIEWS),
  ...Object.keys(ART_VIEWS),
]);
const ADDED_ID = /^[a-f0-9]{24}$/i;
// Numbered items first, in order ("Action Song 1, 2 ..."), then the rest as they came.
const byNumber = (a, b) => (a.sessionNumber || Infinity) - (b.sessionNumber || Infinity);

/**
 * Built-in rows show the published course content placed in them or in the rows under them
 * (Action Songs = its own + Vocal + Instrumental + subcategories added there):
 * - a row without a screen of its own (English > Scenes > Beginners) once any course is placed in
 *   it; until then it stays as it was ("Coming soon!");
 * - a listing screen (Scenes, Action Songs, Stories) always, saying so while it has nothing to list.
 *   A group or row that opened that screen (English opens Scenes, Computer Apps > Artweaver opens
 *   Beginners) opens the content row instead.
 *   The levels of a listing screen (Scenes > Beginners / Intermediate) do the same, so they never
 *   say "Coming soon!" (not even before the courses have loaded);
 * - a group with no rows of its own (Fun with Art): everything placed in its category.
 * `builtInRows` and `subcategories` come from GET .../learning-zone.
 */
export function addBuiltInRowContent(tree, builtInRows = [], subcategories = []) {
  const views = {};

  // Each added subcategory's content counts for the built-in row it sits under, however deep.
  const addedById = new Map(subcategories.map((sub) => [String(sub._id), sub]));
  const builtInAbove = (sub) => {
    let parent = sub.parent || '';
    for (let hops = 0; ADDED_ID.test(parent) && addedById.has(parent) && hops < 10; hops += 1) {
      parent = addedById.get(parent).parent || '';
    }
    return ADDED_ID.test(parent) ? '' : parent;
  };

  const withContent = tree.map((group) => {
    const category = GROUP_CATEGORIES[group.id];
    if (!category) return group;
    const inCategory = (entry) => lower(entry.category) === lower(category);
    if (!group.children) {
      if (group.view || group.to) return group;
      // A group with no rows or screen of its own (Fun with Art) opens everything placed in its
      // category: right in it (`builtInRows` key = the group id) and in its added subcategories,
      // like a category admins added (addCategoryGroups); until then it says nothing is there yet.
      const view = `row:${group.id}`;
      views[view] = {
        title: group.label,
        category,
        taskType: '',
        chapters: [
          ...builtInRows.filter(inCategory).flatMap((row) => row.chapters || []),
          ...subcategories.filter(inCategory).flatMap((sub) => fromRow(sub.chapters, sub.name)),
        ].sort(byNumber),
      };
      return { ...group, view };
    }
    const placed = new Map(builtInRows.filter(inCategory).map((row) => [row.key, row.chapters || []]));
    const fromAdded = new Map();
    subcategories
      .filter((sub) => lower(sub.category) === lower(category))
      .forEach((sub) => {
        const anchor = builtInAbove(sub);
        if (anchor) fromAdded.set(anchor, [...(fromAdded.get(anchor) || []), ...fromRow(sub.chapters, sub.name)]);
      });

    const replaced = new Map(); // old view -> content view
    // Returns the row (maybe with a content view) and every chapter at or under it.
    // `inListing`: the row is a level of a listing screen (Scenes > Beginners).
    const visit = (row, inListing = false) => {
      const children = (row.children || []).map((child) => visit(child, inListing || LISTING_VIEWS.has(row.view)));
      const chapters = [
        ...fromRow(placed.get(row.id), row.label),
        ...(fromAdded.get(row.id) || []),
        ...children.flatMap((child) => child.chapters),
      ].sort(byNumber);
      let next = row.children ? { ...row, children: children.map((child) => child.row) } : row;
      const view = `row:${row.id}`;
      if ((row.children || []).some((child) => child.view === row.view) && replaced.has(row.view)) {
        // A row that opens one of its own rows (Artweaver opens Beginners) opens that row's content.
        next = { ...next, view: replaced.get(row.view) };
      } else if (!row.view && !row.to && (inListing || placed.has(row.id) || chapters.length)) {
        views[view] = { title: row.label, category, taskType: '', chapters };
        next = { ...next, view };
      } else if (LISTING_VIEWS.has(row.view)) {
        views[view] = { title: row.label, category, taskType: '', chapters };
        replaced.set(row.view, view);
        next = { ...next, view };
      }
      return { row: next, chapters };
    };

    const children = group.children.map((child) => visit(child).row);
    return { ...group, ...(replaced.has(group.view) ? { view: replaced.get(group.view) } : {}), children };
  });

  return { tree: withContent, views };
}

/**
 * Gives each content view the look of the screen it belongs to (`screen`), so Action Songs and
 * Stories keep their own design while showing the published content (CourseContentView):
 * - Action Songs, Vocal / Instrumental Songs and every row added under them: { kind: 'songs' };
 * - every row in the Stories group (its lists, task type rows, added subcategories):
 *   { kind: 'stories' }, Balgopal / Saint / 2 min Balgruha Stories with their own options
 *   (`preset`, their STORY_VIEWS id);
 * - Computer Apps > Artweaver > Beginners / Intermediate (Art Therapy's until 2026-10-26):
 *   { kind: 'art', preset } (an ART_VIEWS id). The rows under or beside them keep the Scenes look
 *   with Your Practice, as before;
 * - the rest (Scenes and its levels, the other task type rows and subcategories): no `screen`,
 *   i.e. the Scenes look.
 * `icon` is the row's sidebar icon. `baseTree` is the tree before any view was swapped for a
 * content view (SIDEBAR_TREE), which says which row had which screen.
 */
export function addContentScreens(baseTree, tree, views) {
  const baseViews = new Map();
  const collect = (row) => {
    if (row.view) baseViews.set(row.id, row.view);
    (row.children || []).forEach(collect);
  };
  baseTree.forEach(collect);

  const withScreens = { ...views };
  const visit = (row, inherited) => {
    const base = baseViews.get(row.id);
    let screen = inherited;
    if (STORY_VIEWS[base]) screen = { kind: 'stories', preset: base };
    else if (ACTION_SONG_VIEWS[base]) screen = { kind: 'songs', preset: base };
    else if (ART_VIEWS[base]) screen = { kind: 'art', preset: base };
    // A group shares its view with the row it opens (Stories > Balgopal Stories), which comes next.
    if (screen && withScreens[row.view]) withScreens[row.view] = { ...withScreens[row.view], screen: { ...screen, icon: row.icon } };
    // The art screen has no Your Practice, so the rows around it don't take its look.
    const passedOn = screen && screen.kind !== 'art' ? { kind: screen.kind } : null;
    (row.children || []).forEach((child) => visit(child, passedOn));
  };
  tree.forEach((group) => visit(group, group.id === 'stories' ? { kind: 'stories' } : null));

  return withScreens;
}

/**
 * The course categories admins added (Courses page > "+ Add Category", e.g. Music) as sidebar groups
 * of their own, after Fun with Art (the admin Courses sidebar's order). Each opens its published
 * content, together with what its subcategories hold (like Scenes and its levels); the subcategories
 * become rows under it (addCustomSubcategoryRows, with `groupCategories`). Until anything is added,
 * it says so. `categoryGroups` and `subcategories` come from GET .../learning-zone.
 * Returns { tree, views, groupCategories } (group id -> category).
 */
export function addCategoryGroups(tree, categoryGroups = [], subcategories = []) {
  const views = {};
  const groupCategories = {};
  const groups = categoryGroups.map((category) => {
    const id = `category-${category.id}`;
    const view = `category:${category.id}`;
    groupCategories[id] = category.name;
    views[view] = {
      title: category.name,
      category: category.name,
      taskType: '',
      chapters: [
        ...(category.chapters || []),
        ...subcategories.filter((sub) => lower(sub.category) === lower(category.name)).flatMap((sub) => fromRow(sub.chapters, sub.name)),
      ],
    };
    return { id, label: category.name, icon: category.icon || '📚', ...(category.iconUrl ? { iconUrl: category.iconUrl } : {}), view };
  });
  if (!groups.length) return { tree, views, groupCategories };

  const artTherapy = tree.findIndex((group) => group.id === 'art-therapy');
  const at = artTherapy === -1 ? tree.length : artTherapy + 1;
  return { tree: [...tree.slice(0, at), ...groups, ...tree.slice(at)], views, groupCategories };
}

/**
 * The Learning Zone sidebar: the built-in rows (with the content placed in them, see
 * addBuiltInRowContent), then the courses' task type rows, the categories admins added (see
 * addCategoryGroups), then the added subcategories. The lists come from GET .../learning-zone.
 * Each view says which screen's look it has (addContentScreens).
 */
export function buildLearningZoneTree(baseTree, categories = [], subcategories = [], builtInRows = [], categoryGroups = []) {
  const withBuiltIn = addBuiltInRowContent(baseTree, builtInRows, subcategories);
  const withTaskTypes = addCourseTaskTypeRows(withBuiltIn.tree, categories);
  const withCategories = addCategoryGroups(withTaskTypes.tree, categoryGroups, subcategories);
  const withAdded = addCustomSubcategoryRows(withCategories.tree, subcategories, {
    ...GROUP_CATEGORIES,
    ...withCategories.groupCategories,
  });
  return {
    tree: withAdded.tree,
    views: addContentScreens(baseTree, withAdded.tree, {
      ...withBuiltIn.views,
      ...withTaskTypes.views,
      ...withCategories.views,
      ...withAdded.views,
    }),
  };
}
